import axios from "axios";
import { API_KEY } from "../config/appConfig";
import { Toaster } from "../shared/components/common/toaster";
import { CultureHelper } from "../helpers/cultureHelper";
import {
  getExtractedUserFromToken,
  Logout,
  refreshTokenIfNecessary,
  saveToken,
  setUser,
} from "./authService";
import { buildLocalizeRoute } from "../helpers/languageHelper";
import { AppRoutes } from "../constants/appRoutes";

let retry = false;

const cancelTokens = [];

const redirectToLogin = () => {
  Logout();
  window.location.href = buildLocalizeRoute(AppRoutes.Login);
};

const axiosNew = axios.create({
  baseURL: `${API_KEY}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: function (status) {
    if (status === 403 || status === 401) {
      cancelTokens.forEach((tokenSource) => tokenSource.cancel());
      cancelTokens.length = 0;
      Toaster({ msg: "Session expired, Please login!" });
      redirectToLogin();
      return;
    }
    if (status >= 200 && status < 300) {
      return true;
    }

    return false;
  },
});

axiosNew.interceptors.request.use(async function (config) {
  const cancelTokenSource = axios.CancelToken.source();
  config.cancelToken = cancelTokenSource.token;
  cancelTokens.push(cancelTokenSource);
  config.params = {
    ...config.params,
    ...{ culture: CultureHelper.currentCulture() },
  };
  const token = localStorage.getItem("token");
  const refreshedToken = await refreshTokenIfNecessary(token);
  config.headers.Authorization = refreshedToken
    ? `Bearer ${refreshedToken}`
    : "";
  return config;
});

axiosNew.interceptors.response.use(
  (res) => {
    if (
      res.status >= 200 &&
      res.status < 300 &&
      res.config.method !== "get" &&
      !res.config?.headers?.hide_success_toaster
    ) {
      // Toaster({ msg: 'Request Successfully Completed', isError: false });
    }
    return res;
  },
  async (error) => {
    const originalConfig = error.config;
    if (
      originalConfig?.url !== buildLocalizeRoute(AppRoutes.Login) &&
      error.response
    ) {
      if (error.response.status === 401 && !retry) {
        retry = true;
        try {
          const response = await axiosNew.post(
            "/authentication/refreshtoken",
            null,
            {
              responseType: "text",
            }
          );
          if (response.status === 401) {
            cancelTokens.forEach((tokenSource) => tokenSource.cancel());
            cancelTokens.length = 0;
            Toaster({ msg: "Session expired, Please login!" });
            Logout();
            window.location.href = buildLocalizeRoute(AppRoutes.Login);
            return;
          } else if (response) {
            saveToken(response);
            const user = getExtractedUserFromToken();
            setUser(user);
            return axiosNew(originalConfig);
          }
        } catch (_err) {
          return Promise.reject(_err);
        }
      }
    }
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      cancelTokens.forEach((tokenSource) => tokenSource.cancel());
      cancelTokens.length = 0;
      return;
    }
    if (
      !error?.config?.headers?.hide_toaster &&
      !error?.response?.data?.UserLockedOut &&
      !error?.response?.data?.loginEmailNotFound
    ) {
      if (axios.isCancel(error)) {
        return;
      }

      if (error.response?.status === 403) {
        cancelTokens.forEach((tokenSource) => tokenSource.cancel());
        cancelTokens.length = 0;
        Toaster({
          msg: formatError(error?.response?.data) || "Access Denied!",
        });
      } else {
        cancelTokens.forEach((tokenSource) => tokenSource.cancel());
        cancelTokens.length = 0;
        if (
          error?.response?.data?.IbanIsNullOrEmpty ||
          error?.response?.data?.BankNameIsNullOrEmpty ||
          error?.response?.data?.insufficientFunds
        ) {
        } else {
          Toaster({
            msg:
              formatError(error?.response?.data) ||
              "Something went wrong. Please try again",
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

export const axiosGet = (params, url, id, responseType = "", headers) => {
  let fullUrl = id ? `${url}/${id}` : url;
  return axiosNew.get(fullUrl, {
    params: params,
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPost = (body, url, headers, responseType) => {
  return axiosNew.post(url, body, {
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPut = (body, url, headers, responseType) => {
  return axiosNew.put(url, body, {
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPatch = (
  url,
  body,
  headers,
  responseType,
  onUploadProgress
) => {
  return axiosNew.patch(url, body, {
    headers: headers,
    responseType: responseType,
    onUploadProgress: onUploadProgress,
  });
};

export const axiosDelete = (url, headers, responseType) => {
  return axiosNew.delete(url, {
    headers: headers,
    responseType: responseType,
  });
};

const formatErrorObject = (error) => {
  const keys = Object.keys(error);
  const errors = keys.map((k) => error[k]);
  if (errors.join("<br />") !== "[object Object]") {
    return errors.join("<br />");
  }
  if (errors.length && error[0]?.message) {
    return error[0]?.message;
  }
  return null;
};

export const formatError = (error) => {
  if (!error) {
    return "Something went wrong. Please try again";
  }

  if (typeof error === "string") {
    let parseValues = JSON.parse(error);
    if (Object.keys(parseValues).length) {
      return formatErrorObject(parseValues);
    }
    return error;
  }

  if (error?.errors && typeof error?.errors == "object") {
    return formatErrorObject(error?.errors);
  }

  if (typeof error == "object") {
    return formatErrorObject(error);
  }

  return error;
};

export { axiosNew };
